import { SpanningTree, NodeContainer } from "../Topology/SpanningTree";
import { TopoEdgeSet } from "../Topology/TopoEdgeSet";
import { TopoNodeSet } from "../Topology/TopoNodeSet";
import { LayoutSettings, LayoutType } from "../Topology/LayoutSettings";
import { RadialLayout, RadialLayoutSettings } from "../Topology/RadialLayout";
import { HierarchicalLayout, HierarchicalLayoutSettings } from "../Topology/HierarchicalLayout";
import { GridLayout } from "../Topology/GridLayout";
import { RingLayout } from "../Topology/RingLayout";
import { TopoNode } from "../Topology/TopoNode";
import { List } from "../Utils/List";
import { HashSet } from "../Utils/HashSet";
import { DeviceGroup, DeviceGroupType } from "../Core/DeviceGroup";
import { DeviceNode } from "../Topology/DeviceNode";
import { MultiNode, MultiNodeType } from "../Topology/MultiNode";
import { TopoEdge } from "../Topology/TopoEdge";
import { LayoutCluster, ILayoutCluster } from "../Topology/LayoutCluster";
import { Link, LinkType } from "../Core/Link";
import { DeviceCategory } from "../Core/DeviceCategory";
import { DeviceCategories } from "../Core/DeviceCategories";
import { AssociatedConnection } from "../Topology/AssociatedConnection";
import { Device } from "../Core/Device";
import { DeviceAssociations } from "../Topology/DeviceAssociations";
import { Dictionary } from "../Utils/Dictionary";
import { LinkMember } from "../Core/LinkMember";
import { MultiNodeConnection } from "../Topology/MultiNodeConnection";
import { DeviceConnection } from "../Topology/DeviceConnection";
import { DeviceGroupMember } from "../Core/DeviceGroupMember";
import { MembershipType } from "../Topology/MembershipType";
import { MembershipCount } from "../Topology/MembershipCount";
import { ObjUtils, Iterable, IComparable } from "../Utils/ObjUtils";
import { DeviceGroupNode } from "./DeviceGroupNode";

export class Tuple<K, T>{
	constructor(
		public Item1: K,
		public Item2: T) {

	}
}

//export class TopoMapJson {
//    layout_settings: LayoutSettingsJson;
//    //image_layer: ImageLayerJson;
//    topo_nodes: TopoNodeSetJson;
//}

export class TopoMap extends NodeContainer {

	private _spanningTrees: List<SpanningTree>;
	private _unconnectedNodes: HashSet<TopoNode>;
	private _topoEdges: TopoEdgeSet;
	private _topoNodes: TopoNodeSet;

	constructor(group: DeviceGroup = null) {
		super();
		this.Group = group;
		this._topoEdges = new TopoEdgeSet();
		this._topoNodes = new TopoNodeSet();
		this._spanningTrees = new List<SpanningTree>();
		this._unconnectedNodes = new HashSet<TopoNode>();

		this.ClearSettings();
	}

	public Clear(): void {
		this.ClearDevices();
		this.ClearSettings();
	}

	private ClearSettings(): void {
		this.LayoutSettings = new LayoutSettings();
	}

	private ClearDevices(): void {
		this._topoEdges.Clear();
		this._topoNodes.Clear();
		this._unconnectedNodes.Clear();
		this._spanningTrees.Clear();
	}

	public Group: DeviceGroup;
	public LayoutSettings: LayoutSettings;

	public get DeviceNodes(): List<DeviceNode> {
		return this._topoNodes.DeviceNodes;
	}

	public get DeviceGroupNodes(): List<DeviceGroupNode> {
		return this._topoNodes.DeviceGroupNodes;
	}

	public get MultiNodes(): List<MultiNode> {
		return this._topoNodes.MultiNodes;
	}

	public get VisibleNodes(): List<TopoNode> {
		var result = new List<TopoNode>();

		this._topoNodes.DeviceNodes.ForEach((node) => {
			if (node.HasDeviceGroupMember)
				result.Add(node);
		});

		this._topoNodes.DeviceGroupNodes.ForEach((node) => {
			if (node.HasDeviceGroup)
				result.Add(node);
		});

		this._topoNodes.MultiNodes.ForEach((node) => {
			if (node.HasLink)
				result.Add(node);
		});

		return result;
	}

	public get TopoEdges(): List<TopoEdge> {
		return this._topoEdges.ToList();
	}

	public NodeEdges(node: TopoNode): List<TopoEdge> {
		return this._topoEdges.NodeEdges(node);
	}

	public get IsManualLayout(): boolean {
		return this.LayoutSettings.IsManualLayout;
	}

	public ShiftToZero(margin = 0): void {
		this.CalculateSize();

		this.Offset(-this.Left + margin, -this.Top + margin);

		this.CalculateSize();
	}

	public Flip(): void {
		var bottom: number = this.Bottom;

		this._topoNodes.ToList().ForEach((node) => {
			node.Y = bottom - (node.Y + node.Height);
		});
	}

	public Offset(x: number, y: number): void {
		this._topoNodes.ToList().ForEach((node) => {
			node.Offset(x, y);
		});
	}

	public NodeAngle(node: TopoNode): number {
		var result = 0;

		var parent: TopoNode = this.ParentNode(node);

		if (parent != null) {
			var deltaX = node.X - parent.X;
			var deltaY = parent.Y - node.Y;

			result = (Math.atan2(deltaY, deltaX) * 180 / Math.PI);

			if (result < 0)
				result += 360;
		}

		return result;
	}

	public LayoutMap(settings: LayoutSettings): void {
		this.LayoutMapNodes(this.VisibleNodes, settings);
	}

	public LayoutChildren(node: TopoNode, layoutSettings: LayoutSettings): ILayoutCluster {
		var result: ILayoutCluster = null;

		if (layoutSettings.LayoutType == LayoutType.Radial) {
			var radialLayout = new RadialLayout(this);
			radialLayout.Settings = layoutSettings.RadialSettings;
			result = radialLayout.LayoutChildren(node);
		}
		else if (layoutSettings.LayoutType == LayoutType.Hierarchical) {
			var hierarchicalLayout = new HierarchicalLayout(this);
			hierarchicalLayout.Settings = layoutSettings.HierarchicalSettings;
			result = hierarchicalLayout.LayoutChildren(node);
		}

		return result;
	}

	private LayoutMapNodes(newNodes: List<TopoNode>, settings: LayoutSettings): void {
		this.ResolveRoots();
		this.PopulateSpanningTrees();

		if (settings.LayoutType == LayoutType.Radial) {
			var radialLayout = new RadialLayout(this);
			radialLayout.Settings = settings.RadialSettings;
			radialLayout.LayoutSpanningTrees(this.SpanningTrees);

			var gridLayout1 = new GridLayout(this);
			gridLayout1.LayoutNodes(this.UnconnectedNodes);
		}
		else if (settings.LayoutType == LayoutType.Hierarchical) {
			var hierarchicalLayout = new HierarchicalLayout(this);
			hierarchicalLayout.Settings = settings.HierarchicalSettings;
			hierarchicalLayout.LayoutSpanningTrees(this.SpanningTrees);

			var gridLayout2 = new GridLayout(this);
			gridLayout2.LayoutNodes(this.UnconnectedNodes);
		}
		else if (settings.LayoutType == LayoutType.Ring) {
			var ringLayout = new RingLayout(this);
			ringLayout.Settings = settings.RingSettings;
			ringLayout.LayoutSpanningTrees(this.SpanningTrees);

			var gridLayout4 = new GridLayout(this);
			gridLayout4.LayoutNodes(this.UnconnectedNodes);
		}
		else if (settings.LayoutType == LayoutType.Manual) {
			var gridLayout3 = new GridLayout(this);
			gridLayout3.LayoutNodes(newNodes);
		}

		this.CalculateSize();
	}

	private ResolveRoots(): void {
		var roots: List<TopoNode> = this.LayoutSettings.RootNodes.ToList();
		this.LayoutSettings.RootNodes.Clear();

		roots.ForEach((node) => {
			var resolvedNode: TopoNode = this._topoNodes.FindNode(node);
			if (resolvedNode != null)
				this.LayoutSettings.RootNodes.Add(resolvedNode);
		});
	}

	public CalculateSize(): void {
		var nodes: List<TopoNode> = this.VisibleNodes;
		//nodes.AddRange(ImageLayer.ImageNodes);
		super.CalculateSize(nodes);
	}

	public Unload(): void {
		this._topoNodes.UnResolve();
		this._topoEdges.Clear();
		this._spanningTrees.Clear();
		this._unconnectedNodes.Clear();
	}

	/// <summary>
	/// 
	/// </summary>
	/// <param name="removeUnresolvedNodes"></param>
	/// <param name="layoutMap"></param>
	/// <returns>a collection of the new nodes</returns>
	public Rebuild(layoutMap: boolean = true): List<TopoNode> {
		var existingNodes: List<TopoNode> = this._topoNodes.ToList();

		this.Unload();

		this.Group.Members.ForEach((deviceMember) => {
			var node = new DeviceNode(deviceMember);
			node = this._topoNodes.AddOrResolve_Device(node);
		});

		this.Group.ChildGroups.ForEach((childGroup) => {
			var node = new DeviceGroupNode(childGroup);
			node = this._topoNodes.AddOrResolve_DeviceGroup(node);
		});

		this.RemoveUnresolvedNodes();

		var links = new HashSet<Link>();
		this._topoNodes.DeviceNodes.ForEach((node) => {
			if (node.HasDevice) {
				node.Device.AllLinks.ForEach((link) => {
					links.Add(link);
				});
			}
		});

		// Add Link Edges
		links.ForEach((link) => {
			this.AddLink(link);
		});


		var resolvedMultiNodes: List<MultiNode> = this.ResolveMultiNodes();
		resolvedMultiNodes.ForEach((node) => {
			existingNodes.Add(node);
		});

		this.AddAssociatedEdges();

		var newNodes: List<TopoNode> = this._topoNodes.ToList().Except(existingNodes);
		if (layoutMap)
			this.LayoutMapNodes(newNodes, this.LayoutSettings);

		return newNodes;
	}

	private RemoveUnresolvedNodes(): void {
		if (this.Group.Type == DeviceGroupType.Static) {
			var unresolvedNodes = new List<DeviceNode>();
			this._topoNodes.DeviceNodes.ForEach((node) => {
				if (!node.HasDeviceGroupMember)
					unresolvedNodes.Add(node);
			});

			unresolvedNodes.ForEach((node) => {
				this.RemoveNode(node);
			});
		}
	}

	private AddAssociatedEdges(): void {
		this._topoNodes.DeviceNodes.ForEach((sourceNode) => {
			if (!sourceNode.HasDevice)
				return;

			if (this.LayoutSettings.ShowVirtualLinks)
				this.AddAssociations(sourceNode, DeviceCategory.VirtualHost);
			if (this.LayoutSettings.ShowWirelessLinks)
				this.AddAssociations(sourceNode, DeviceCategory.WirelessAp);
			if (this.LayoutSettings.ShowIpPhoneLinks)
				this.AddAssociations(sourceNode, DeviceCategory.IpPhone);
		});
	}

	private AddAssociations(sourceNode: DeviceNode, category: DeviceCategory): void {
		var sourceCon = new AssociatedConnection(sourceNode, category);

		let srcDevice: Device = sourceNode.Device;
		if (srcDevice && srcDevice.Categories) {
			let srcCategory: DeviceCategory = null;

			let categories: DeviceCategories = srcDevice.Categories;
			if (categories.Contains(DeviceCategory.IpPhoneManager))
				srcCategory = DeviceCategory.IpPhoneManager;
			else if (categories.Contains(DeviceCategory.IpPhone))
				srcCategory = DeviceCategory.IpPhone;
			else if (categories.Contains(DeviceCategory.WirelessController))
				srcCategory = DeviceCategory.WirelessController;
			else if (categories.Contains(DeviceCategory.WirelessAp))
				srcCategory = DeviceCategory.WirelessAp;
			else if (categories.Contains(DeviceCategory.VirtualServer))
				srcCategory = DeviceCategory.VirtualServer;
			else if (categories.Contains(DeviceCategory.VirtualHost))
				srcCategory = DeviceCategory.VirtualHost;
			else if (categories.Contains(DeviceCategory.Virtual) ||
				categories.Contains(DeviceCategory.VirtualMachine))
				srcCategory = DeviceCategory.Virtual;

			if (srcCategory != null)
				sourceCon.Category = srcCategory;
		}

		//if (category == DeviceCategory.IpPhone)
		//	sourceCon.Category = DeviceCategory.IpPhoneManager;
		//else if (category == DeviceCategory.Virtual)
		//	sourceCon.Category = DeviceCategory.VirtualServer;
		//else if (category == DeviceCategory.VirtualServer) {
		//	if (sourceNode.Device.HasVirtualMachineInfo)
		//		sourceCon.Category = DeviceCategory.Virtual;
		//	else if (sourceNode.Device.HasVirtualHostTable)
		//		sourceCon.Category = DeviceCategory.VirtualHost;
		//}
		//else if (category == DeviceCategory.VirtualHost)
		//	sourceCon.Category = DeviceCategory.VirtualServer;
		//else if (category == DeviceCategory.WirelessController)
		//	sourceCon.Category = DeviceCategory.WirelessAp;
		//else if (category == DeviceCategory.WirelessAp)
		//	sourceCon.Category = DeviceCategory.WirelessController;

		var devices: HashSet<Device> = DeviceAssociations.AssociatedDevices(sourceNode.Device, category);
		devices.ForEach((device) => {
			var targetNode: DeviceNode = this._topoNodes.FindNode_Device(device);
			if (targetNode == null || targetNode == sourceNode)
				return;

			var targetCon = new AssociatedConnection(targetNode, category);
			this._topoEdges.AddTopoLinkMember(sourceCon, targetCon);
		});
	}

	private ResolveMultiNodes(): List<MultiNode> {
		var result = new List<MultiNode>();

		// If a multi-node does not have a link it did not
		// resolve with the current link set
		// For all multi-nodes without a link
		// find a multi-node with a link and the most intersecting
		// link members and resolve to that multi-node
		this._topoNodes.MultiNodes.ForEach((node) => {
			if (node.HasLink)
				return;

			var match: MultiNode = null;
			var matchStrength = 0;
			this._topoNodes.MultiNodes.ForEach((node2) => {
				if (!node2.HasLink)
					return;

				var strength = node.Key.Keys.Intersect(node2.Key.Keys).Count;
				if (strength > matchStrength) {
					matchStrength = strength;
					match = node2;
				}
			});

			if (match != null) {
				match.Location = node.Location;
				result.Add(match);
			}
		});

		// Remove any multi-nodes that didn't resolve and whose devices are not 
		// present in the map
		this._topoNodes.MultiNodes.ForEach((node) => {
			if (!node.HasLink) {
				var presentDeviceCount = 0;
				node.Key.Keys.ForEach((devKey) => {
					if (this._topoNodes.ContainsDeviceNode_Key(devKey))
						presentDeviceCount++;
				});

				if (presentDeviceCount < 2)
					this.RemoveNode(node);
			}
		});

		return result;
	}

	private AddLink(link: Link): void {
		if (!this.LayoutSettings.ShowLayer2Links && link.LinkType != LinkType.Manual)
			return;

		var deviceLinkMembers = new Dictionary<Device, List<LinkMember>>();
		link.LinkMembers.ForEach((linkMember) => {
			if (!this._topoNodes.ContainsDeviceNode(linkMember.Device))
				return;

			if (!deviceLinkMembers.ContainsKey(linkMember.Device))
				deviceLinkMembers.Add(linkMember.Device, new List<LinkMember>());

			deviceLinkMembers.Item(linkMember.Device).Add(linkMember);
		});

		if (deviceLinkMembers.Count > 1) {
			if (link.LinkType == LinkType.WirelessEndNode)
				this.AddWirelessLink(link, deviceLinkMembers);
			else if (link.LinkType == LinkType.VirtualMachine)
				this.AddVirtualLink(link, deviceLinkMembers);
			else if (link.LinkMembers.Count > 2 && deviceLinkMembers.Count >= 2)
				this.AddMultiMemberDeviceLink(link, deviceLinkMembers);
			else if (deviceLinkMembers.Count == 2)
				this.AddTwoMemberDeviceLink(link, deviceLinkMembers);
		}
	}

	private AddWirelessLink(link: Link, deviceLinkMembers: Dictionary<Device, List<LinkMember>>): void {
		var ssidNodes = new Dictionary<string, Tuple<MultiNode, MultiNodeConnection>>();

		var nodeTuple: Tuple<MultiNode, MultiNodeConnection>;
		var wirelessNode: MultiNode;
		var wirelessConnection: MultiNodeConnection;

		deviceLinkMembers.Keys().ForEach((device) => {
			var deviceNode = this._topoNodes.FindNode_Device(device);
			// Device not on map
			if (deviceNode == null)
				return;

			var linkMembers = deviceLinkMembers.Item(device);
			var member: LinkMember = linkMembers.Get(0);

			if (member == null)
				return;

			if (member.SSID == null)
				member.SSID = '';

			nodeTuple = ssidNodes.Item(member.SSID);
			if (nodeTuple == null) {
				wirelessNode = new MultiNode(link, MultiNodeType.WirelessClient);
				wirelessNode.Description = "SSID: " + member.SSID;
				wirelessNode = this._topoNodes.AddOrResolve_Multi(wirelessNode);
				wirelessConnection = new MultiNodeConnection(wirelessNode, link);
				if (member.HasIfEntry && member.IfEntry.HasDescription)
					wirelessConnection.Description = member.IfEntry.DisplayName;
				ssidNodes.Add(member.SSID, new Tuple<MultiNode, MultiNodeConnection>(wirelessNode, wirelessConnection));
			}
			else {
				wirelessNode = nodeTuple.Item1;
				wirelessConnection = nodeTuple.Item2;
			}

			var deviceConnection = new DeviceConnection(deviceNode, link, linkMembers);
			this._topoEdges.AddTopoLinkMember(deviceConnection, wirelessConnection);
		});
	}

	private AddVirtualLink(link: Link, deviceLinkMembers: Dictionary<Device, List<LinkMember>>): void {

		let portGroupNodes = new Dictionary<string, Tuple<MultiNode, MultiNodeConnection>>();

		let nodeTuple: Tuple<MultiNode, MultiNodeConnection>;
		let portGroupNode: MultiNode;
		let portGroupConnection: MultiNodeConnection;

		deviceLinkMembers.Keys().ForEach((device: Device) => {
			let deviceNode: DeviceNode = this._topoNodes.FindNode_Device(device);
			// Device not on map
			if (deviceNode == null)
				return;

			let linkMembers: List<LinkMember> = deviceLinkMembers.Item(device);
			let member: LinkMember = linkMembers.Get(0);

			if (member == null)
				return;

			if (member.VirtualPortGroup == null)
				member.VirtualPortGroup = '';

			nodeTuple = portGroupNodes.Item(member.VirtualPortGroup);
			if (nodeTuple == null) {
				portGroupNode = new MultiNode(link, MultiNodeType.VirtualPortGroup);
				portGroupNode.Description = "Port Group: " + member.VirtualPortGroup;
				portGroupNode = this._topoNodes.AddOrResolve_Multi(portGroupNode);
				portGroupConnection = new MultiNodeConnection(portGroupNode, link);
				if (member.HasVirtualPort)
					portGroupConnection.Description = member.VirtualPort;
				portGroupNodes.Add(member.VirtualPortGroup,
					new Tuple<MultiNode, MultiNodeConnection>(portGroupNode, null /*portGroupConnection*/));
			}
			else {
				portGroupNode = nodeTuple.Item1;
				//portGroupConnection = nodeTuple.Item2;

				portGroupConnection = new MultiNodeConnection(portGroupNode, link);
				if (member.HasVirtualPort)
					portGroupConnection.Description = member.VirtualPort;
			}

			let deviceConnection = new DeviceConnection(deviceNode, link, linkMembers);
			this._topoEdges.AddTopoLinkMember(deviceConnection, portGroupConnection);
		}, this);
	}

	private AddTwoMemberDeviceLink(link: Link, deviceLinkMembers: Dictionary<Device, List<LinkMember>>):void {
		var localDevice: Device = deviceLinkMembers.Keys().Get(0);
		var localNode: DeviceNode = this._topoNodes.FindNode_Device(localDevice);
		var localConnection = new DeviceConnection(localNode, link, deviceLinkMembers.Item(localDevice));

		var remoteDevice :Device = deviceLinkMembers.Keys().Get(1);
		var remoteNode: DeviceNode = this._topoNodes.FindNode_Device(remoteDevice);
		var remoteConnection = new DeviceConnection(remoteNode, link, deviceLinkMembers.Item(remoteDevice));

		this._topoEdges.AddTopoLinkMember(localConnection, remoteConnection);
	}

	private AddMultiMemberDeviceLink(link: Link, deviceLinkMembers: Dictionary<Device, List<LinkMember>>):void {
		var multiNode = new MultiNode(link);
		multiNode = this._topoNodes.AddOrResolve_Multi(multiNode);
		var multiConnection = new MultiNodeConnection(multiNode, link);
		multiConnection.Description = "";

		deviceLinkMembers.Keys().ForEach((device) => {
			var linkMembers: List<LinkMember> = deviceLinkMembers.Item(device);

			var deviceNode: DeviceNode = this._topoNodes.FindNode_Device(device);
			// Device not on map
			if (deviceNode == null)
				return;

			var deviceConnection = new DeviceConnection(deviceNode, link, linkMembers);
			this._topoEdges.AddTopoLinkMember(deviceConnection, multiConnection);
		});
	}

	public RemoveNode_GroupMember(member: DeviceGroupMember) :void {
		if (member == null)
			return;

		var node: DeviceNode = this._topoNodes.FindNode_DeviceKey(member.Key);
		if (node != null)
			this.RemoveNode(node);
	}

	public RemoveNode_Device(device: Device):void {
		var node: DeviceNode = this._topoNodes.FindNode_Device(device);
		if (node != null)
			this.RemoveNode(node);
	}

	public RemoveNode(node: TopoNode):void {
		this._topoNodes.RemoveNode(node);
		this.LayoutSettings.RootNodes.Remove(node);

		if (node instanceof DeviceNode)
		{
			var deviceNode = <DeviceNode> node;

			this.Group.RemoveMember(deviceNode.DeviceGroupMember);
		}
		else if (node instanceof MultiNode)
		{
			this.ChildNodes(node).ForEach((child) => {
				this.RemoveNode(child);
			});
		}
		//else if (node instanceof ImageNode)
		//{
		//	ImageLayer.RemoveImageNode(node as ImageNode);
		//}
	}

	public AddSelectedMapMembers(devices: List<Device>, settings: LayoutSettings): void {
		devices.ForEach((device) => {
			this.Group.AddMember(new DeviceGroupMember(device));
		});

		if (this.LayoutSettings.IsManualLayout) {
			var newNodes: List<TopoNode> = this.Rebuild(false);
			this.ResolveRoots();
			this.PopulateSpanningTrees();

			var unconnectedNodes = new List<TopoNode>();

			if (settings.IsManualLayout) {
				unconnectedNodes.UnionWith(newNodes);
			}
			else {
				this.CalculateSize();
				var curRight :number = this.Right;

				var newClusters = new Dictionary<TopoNode, ILayoutCluster>();

				devices.ForEach((device) => {
					var node: DeviceNode = this._topoNodes.FindNode_Device(device);
					if (node == null)
						return;

					// Check if this node gets layed out by parent.
					var parent: TopoNode = this.ParentNode(node);
					if (parent != null) {
						// If the parent is a multi-node check its parent
						if (parent instanceof MultiNode) {
							// If the multi-node parent is new, this multi-node and device will get layed out by it
							var multiNodeParent = this.ParentNode(parent);
							if ((multiNodeParent instanceof DeviceNode) && newNodes.Contains(multiNodeParent))
								return;

							// If the multi-node is new layout its children
							if (newNodes.Contains(parent)) {
								// Layout children if they haven't been yet
								if (!newClusters.ContainsKey(parent))
									newClusters.Add(parent, this.LayoutChildren(parent, settings));
							}
							else if (this.ChildNodes(node).Count == 0)
								unconnectedNodes.Add(node);
							else
								newClusters.Add(node, this.LayoutChildren(node, settings));

							return;
						}

						if ((parent instanceof DeviceNode) && devices.Contains((<DeviceNode> parent).Device))
							return;
					}

					if (this.ChildNodes(node).Count == 0)
						unconnectedNodes.Add(node);
					else
						newClusters.Add(node, this.LayoutChildren(node, settings));
				});

				newClusters.Values().ForEach((cluster) => {
					var xOffset = curRight - cluster.Left + RadialLayout.DefaultTreeMargin;
					curRight += cluster.Width + RadialLayout.DefaultTreeMargin;

					cluster.Offset(xOffset, 0);
				});
			}

			if (unconnectedNodes.Count > 0) {
				var layout = new GridLayout(this);
				layout.LayoutNodes(unconnectedNodes);
			}
			this.Rebuild();
		}
		else {
			this.Rebuild();
		}
	}
		
	public AddSelectedNodeMembers(node: TopoNode, devices: List<Device>, settings: LayoutSettings): void {
		devices.ForEach((device) => {
			this.Group.AddMember(new DeviceGroupMember(device));
		});

		this.Rebuild();

		if (this.LayoutSettings.IsManualLayout)
			this.LayoutChildren(node, settings);
	}

	public AddMapMembers(settings: LayoutSettings, mapType: MembershipType, category: DeviceCategory = null):void {
		this._topoNodes.DeviceNodes.ForEach((node) => {
			settings.RadialSettings.LayoutAngle = this.NodeAngle(node);
			this.AddNodeMembers(node, settings, mapType, category);
		});
	}

	public AddNodeMembers(node: TopoNode, settings: LayoutSettings, mapType: MembershipType, category: DeviceCategory = null)
	{
		if (node != null) {
			var members = new List<DeviceGroupMember>();
			var nonMembers = new List<DeviceGroupMember>();

			this.GetNodeMembers(node, members, nonMembers, mapType, category);

			nonMembers.ForEach((nonMember) => {
				this.Group.AddMember(nonMember);
			});

			this.Rebuild();

			if (this.LayoutSettings.IsManualLayout)
				this.LayoutChildren(node, settings);
		}
	}
		
	public RemoveMapMembers(mapType: MembershipType, category: DeviceCategory = null): void {
		this._topoNodes.DeviceNodes.ForEach((node) => {
			this.RemoveNodeMembers(node, mapType, category);
		});
	}

	public RemoveNodeMembers(node: TopoNode, mapType: MembershipType, category: DeviceCategory = null): void	{
		if (mapType == MembershipType.Associated)
			this.RemoveAssociatedNodeMembers(node, category);
		else
			this.RemoveConnectedNodeMembers(node, category);
	}

	private RemoveAssociatedNodeMembers(node: TopoNode, category: DeviceCategory = null)
	{
		if (node instanceof DeviceNode) {
			var devNode = <DeviceNode> node;
			var associated: HashSet<Device> = DeviceAssociations.AssociatedDevices(devNode.Device, category);

			associated.ForEach((device) => {
				var associatedNode: DeviceNode = this._topoNodes.FindNode_Device(device);
				if (associatedNode != null)
					this.RemoveNodeAndDescendants(associatedNode);
			});
		}
	}

	private RemoveConnectedNodeMembers(node: TopoNode, category: DeviceCategory = null):void {
		if (node != null) {
			var children:List<TopoNode> = this.ChildNodes(node);

			children.ForEach((child) => {
				if (category == null)
					this.RemoveNodeAndDescendants(child);
				else if ((child instanceof DeviceNode) && (<DeviceNode>child).HasDevice && (<DeviceNode>child).Device.Categories.Contains(category))
					this.RemoveNodeAndDescendants(child);
			});
		}
	}

	public RemoveNodeAndDescendants(node: TopoNode):void {
		var descendants: List<TopoNode> = this.DescendantNodes(node);
		descendants.ForEach((descendant) => {
			// Leave node on map if it has a remaining neighbor
			var neighbors: List<TopoNode> = this.NeighborNodes(descendant);
			neighbors.Remove(node);
			if (neighbors.Except(descendants).Count == 0)
				this.RemoveNode(descendant);
		});

		this.RemoveNode(node);
	}
		
	public GetMapMembers(members: List<DeviceGroupMember>, nonMembers: List<DeviceGroupMember>, mapType: MembershipType, category: DeviceCategory = null):void {
		var membersResult = new HashSet<DeviceGroupMember>();
		var nonMembersResult = new HashSet<DeviceGroupMember>();

		this._topoNodes.DeviceNodes.ForEach((node) => {
			var childMembers = new List<DeviceGroupMember>();
			var childNonMembers = new List<DeviceGroupMember>();
			this.GetNodeMembers(node, childMembers, childNonMembers, mapType, category);
			membersResult.UnionWith(childMembers);
			nonMembersResult.UnionWith(childNonMembers);
		});

		members.UnionWith(membersResult);
		nonMembers.UnionWith(nonMembersResult);
	}

	public GetNodeMembers(node: TopoNode, members: List<DeviceGroupMember>,
		nonMembers: List<DeviceGroupMember>, mapType: MembershipType, category: DeviceCategory = null)
	{
		var devices = new List<Device>();

		if ((node instanceof DeviceNode) && (<DeviceNode>node).HasDevice) {
			if (mapType == MembershipType.Associated)
				devices.UnionWith(DeviceAssociations.AssociatedDevices((<DeviceNode>node).Device, category));
			else
				devices.UnionWith((<DeviceNode>node).Device.ConnectedByCategory(category));
		}

		if ((node instanceof MultiNode) && (<MultiNode>node).HasLink && mapType == MembershipType.Connected) {
			(<MultiNode>node).Link.LinkMembers.ForEach((member) => {
				if (member.Device == null)
					return;

				if (category == null)
					devices.Add(member.Device);
				else if (member.Device.Categories.Contains(category))
					devices.Add(member.Device);
			});
		}

		var children: List<TopoNode> = this.ChildNodes(node);

		devices.ForEach((device) => {
			if (device == null)
				return;

			var deviceNode: DeviceNode = this._topoNodes.FindNode_Device(device);

			// Device Not on Map
			if (deviceNode == null)
				nonMembers.Add(new DeviceGroupMember(device));
			else if (mapType == MembershipType.Associated)
				members.Add(deviceNode.DeviceGroupMember);
			// Only interested in children, to Ensure node is not parent
			else if (children.Contains(deviceNode))
				members.Add(deviceNode.DeviceGroupMember);
		});
	}

	public MapMembershipCounts(memberType: MembershipType, category: DeviceCategory = null): MembershipCount {
		var members = new List<DeviceGroupMember>();
		var nonMembers = new List<DeviceGroupMember>();

		this.GetMapMembers(members, nonMembers, memberType, category);

		return new MembershipCount(members.Count, nonMembers.Count);
	}

	public NodeMembershipCounts(node: TopoNode, memberType: MembershipType, category: DeviceCategory = null): MembershipCount {
		var members = new List<DeviceGroupMember>();
		var nonMembers = new List<DeviceGroupMember>();

		this.GetNodeMembers(node, members, nonMembers, memberType, category);

		return new MembershipCount(members.Count, nonMembers.Count);
	}
		
	public NeighborNodes(node: TopoNode): List<TopoNode> 
	{
		var result = new List<TopoNode>();

		this._topoEdges.NodeEdges(node).ForEach((edge) => {
			result.Add(edge.PeerNode(node));
		});

		return result;
	}

	public DescendantNodes(node: TopoNode): List<TopoNode> {
		var descendants = new List<TopoNode>();

		var owningTree: SpanningTree = this.OwningTree(node);
		if (owningTree != null)
			descendants.UnionWith(owningTree.DescendantNodes(node));

		return descendants;
	}

	public ChildNodes(node: TopoNode): List<TopoNode> {
		var children = new List<TopoNode>();

		var owningTree: SpanningTree = this.OwningTree(node);
		if (owningTree != null) {
			var treeChildren = owningTree.ChildNodes(node);
			children.UnionWith(treeChildren);

			new List<TopoNode>(children.Values).ForEach((childNode) => {
				if (childNode instanceof MultiNode)
					children.UnionWith(this.ChildNodes(childNode));
			});
		}
		return children;
	}

	public get IsStaticMembership(): boolean {
		if (this.Group != null)
			return this.Group.Type == DeviceGroupType.Static;
		else
			return false;
	}

	public get HasManualRoots(): boolean {
		return this.LayoutSettings.RootNodes.Count > 0;
	}

	public SetAsRoot(node: TopoNode):void {
		var tree: SpanningTree = this.OwningTree(node);

		if (tree != null) {
			this.LayoutSettings.RootNodes.Remove(tree.Root);
			this.LayoutSettings.RootNodes.Add(node);
			tree.Root = node;
		}
	}

	public UnassignAsRoot(node: TopoNode):void {
		this.LayoutSettings.RootNodes.Remove(node);

		var tree: SpanningTree = this.OwningTree(node);

		if (tree != null) {
			tree.PickRoot();
		}
	}

	public IsRoot(node: TopoNode):boolean {
		return this.LayoutSettings.RootNodes.Contains(node);
	}

	public OwningTree(node: TopoNode): SpanningTree {
		var result: SpanningTree = null;
		this._spanningTrees.Some((tree) => {
			if (tree.Contains(node)) {
				result = tree;
				return true;
			}
		});
		return result;
	}
		
	public get SpanningTrees(): List<SpanningTree> {
		return this._spanningTrees;
	}

	public get UnconnectedNodes(): List<TopoNode> {
		return this._unconnectedNodes.ToList();
	}

	public PopulateSpanningTrees():void {
		this._unconnectedNodes.Clear();
		this._spanningTrees.Clear();

		var remainingNodes: List<TopoNode> = this._topoNodes.ToList();
		var rootNodes: List<TopoNode> = this.LayoutSettings.RootNodes.ToList();

		while (remainingNodes.Count > 0) {
			var startNode: TopoNode = null;
			if (rootNodes.Count > 0) {
				startNode = rootNodes.Get(0);
				rootNodes.RemoveAt(0);
			}

			if (!remainingNodes.Contains(startNode))
				startNode = remainingNodes.Get(0);

			if (this.HasAdjacent(startNode)) {
				var tree = new SpanningTree(this, startNode);

				if (!this.LayoutSettings.RootNodes.Contains(startNode))
					tree.PickRoot();

				tree.Nodes.ForEach((node) => {
					remainingNodes.Remove(node);
				});

				this._spanningTrees.Add(tree);
			}
			else {
				this._unconnectedNodes.Add(startNode);
				remainingNodes.Remove(startNode);
			}
		}
	}

	public HasAdjacent(node: TopoNode): boolean {
		return this._topoEdges.NodeHasEdges(node);
	}

	public AdjacentNodes(node: TopoNode, networkNodesOnly: boolean = false): List<TopoNode> {
		var result = new List<TopoNode>();

		this._topoEdges.NodeEdges(node).ForEach((edge) => {
			var peer = edge.PeerNode(node);
			if (networkNodesOnly && !(peer instanceof DeviceNode))
				return;
			result.Add(peer);
		});

		return result;
	}

	public FindNodeByDevice(device: Device): DeviceNode | null {
		if (device == null)
			return null;
		return this._topoNodes.FindNode_Device(device);
	}

	public IsParent(parent: TopoNode, child: TopoNode): boolean {
		var owningTree: SpanningTree = this.OwningTree(child);
		if (owningTree != null)
			return ObjUtils.Equals(owningTree.Parent(child), parent);
		else
			return false;
	}

	public ParentNode(node: TopoNode): TopoNode {
		var owningTree: SpanningTree = this.OwningTree(node);
		if (owningTree != null)
			return owningTree.Parent(node);
		else
			return null;
    }

    //public static FromJson(json: TopoMapJson): TopoMap {
    //    var result = new TopoMap();

    //    if (json.layout_settings !== undefined)
    //        result.LayoutSettings = LayoutSettings.FromJson(json.layout_settings);

    //    //if (json.image_layer !== undefined)
    //    //    result.ImageLayer = ImageLayer.FromJson(json.image_layer);

    //    if (json.topo_nodes !== undefined)
    //        result._topoNodes = TopoNodeSet.FromJson(json.topo_nodes);

    //    result.PopulateSpanningTrees();

    //    return result;
    //}

	public Write(obj: any, context: any): void {
		obj.topo_map = {};
		obj = obj.topo_map;

		this.LayoutSettings.Write(obj, context);

		//this.ImageLayer.Write(obj, context);

		if (this.LayoutSettings.IsManualLayout)
			this._topoNodes.Write(obj, context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.topo_map)
			throw new Error();
		obj = obj.topo_map;

		this.Clear();

		if (obj.layout_settings !== undefined)
			this.LayoutSettings.Read(obj, context);

		if (obj.image_layer !== undefined) {
			//this.ImageLayer.Read(obj, context);
		}

		if (obj.topo_nodes !== undefined)
			this._topoNodes.Read(obj, context);

		this.PopulateSpanningTrees();
	}
}
